import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  format,
  isSameMonth,
  isSameDay,
  isToday,
  addWeeks,
} from 'date-fns';
import { ja } from 'date-fns/locale';
import { TimeRecord } from '@/types';

export type CalendarView = 'month' | 'week' | 'day';

export interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  records: TimeRecord[];
  totalMinutes: number;
}

/**
 * 月間カレンダーの日付配列を生成
 */
export function generateMonthDays(currentDate: Date, records: TimeRecord[]): CalendarDay[] {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 }); // 日曜始まり
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const days: CalendarDay[] = [];
  let day = calendarStart;

  while (day <= calendarEnd) {
    const dayRecords = records.filter((record) =>
      record.clockInTime && isSameDay(record.clockInTime.toDate(), day) && record.status !== 'deleted'
    );
    
    const totalMinutes = dayRecords.reduce(
      (sum, record) => sum + (record.totalMinutes || 0),
      0
    );

    days.push({
      date: day,
      isCurrentMonth: isSameMonth(day, currentDate),
      isToday: isToday(day),
      records: dayRecords,
      totalMinutes,
    });

    day = addDays(day, 1);
  }

  return days;
}

/**
 * 週間カレンダーの日付配列を生成
 */
export function generateWeekDays(currentDate: Date, records: TimeRecord[]): CalendarDay[] {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });

  const days: CalendarDay[] = [];
  let day = weekStart;

  while (day <= weekEnd) {
    const dayRecords = records.filter((record) =>
      record.clockInTime && isSameDay(record.clockInTime.toDate(), day) && record.status !== 'deleted'
    );
    
    const totalMinutes = dayRecords.reduce(
      (sum, record) => sum + (record.totalMinutes || 0),
      0
    );

    days.push({
      date: day,
      isCurrentMonth: true,
      isToday: isToday(day),
      records: dayRecords,
      totalMinutes,
    });

    day = addDays(day, 1);
  }

  return days;
}

/**
 * 日付をフォーマット
 */
export function formatDate(date: Date, formatStr: string): string {
  return format(date, formatStr, { locale: ja });
}

/**
 * 時間を HH:MM 形式にフォーマット
 */
export function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}:${mins.toString().padStart(2, '0')}`;
}

/**
 * 前の期間に移動
 */
export function navigatePrevious(date: Date, view: CalendarView): Date {
  switch (view) {
    case 'month':
      return addMonths(date, -1);
    case 'week':
      return addWeeks(date, -1);
    case 'day':
      return addDays(date, -1);
    default:
      return date;
  }
}

/**
 * 次の期間に移動
 */
export function navigateNext(date: Date, view: CalendarView): Date {
  switch (view) {
    case 'month':
      return addMonths(date, 1);
    case 'week':
      return addWeeks(date, 1);
    case 'day':
      return addDays(date, 1);
    default:
      return date;
  }
}

/**
 * 期間のタイトルを取得
 */
export function getPeriodTitle(date: Date, view: CalendarView): string {
  switch (view) {
    case 'month':
      return formatDate(date, 'yyyy年M月');
    case 'week':
      const weekStart = startOfWeek(date, { weekStartsOn: 0 });
      const weekEnd = endOfWeek(date, { weekStartsOn: 0 });
      return `${formatDate(weekStart, 'M/d')} - ${formatDate(weekEnd, 'M/d')}`;
    case 'day':
      return formatDate(date, 'yyyy年M月d日 (E)');
    default:
      return '';
  }
}

/**
 * プロジェクトのカラーを取得（プロジェクトIDベース）
 */
export function getProjectColor(projectId: string | undefined): string {
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-yellow-500',
    'bg-indigo-500',
    'bg-red-500',
    'bg-teal-500',
  ];
  
  // projectId が undefined または空の場合はデフォルトの色を返す
  if (!projectId || projectId.length === 0) {
    return colors[0]; // デフォルトは青
  }
  
  // プロジェクトIDのハッシュ値から色を決定
  let hash = 0;
  for (let i = 0; i < projectId.length; i++) {
    hash = projectId.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
}

/**
 * 月間の統計を計算
 */
export function calculateMonthStats(records: TimeRecord[]) {
  // 削除済みレコードを除外
  const validRecords = records.filter((record) => record.status !== 'deleted');
  
  const totalMinutes = validRecords.reduce(
    (sum, record) => sum + (record.totalMinutes || 0),
    0
  );
  
  const workDays = new Set(
    validRecords.map((record) => formatDate(record.clockInTime.toDate(), 'yyyy-MM-dd'))
  ).size;
  
  const avgMinutesPerDay = workDays > 0 ? Math.round(totalMinutes / workDays) : 0;
  
  return {
    totalMinutes,
    workDays,
    avgMinutesPerDay,
    totalHours: Math.round(totalMinutes / 60 * 10) / 10,
  };
}
